import { ToastProgrammatic as Toast } from 'buefy';
import dayjs from 'dayjs';
import {
  mapActions,
  mapGetters,
  mapMutations,
  mapState,
} from 'vuex';

import weekdayTransformer from '@/utils/weekdayTransformer';
import { GET_STORE_LIST } from '@/store/store/constants';
import { UPDATE_DRAFT_ORDER } from '@/store/orders/constants';

export default {
  name: 'StoreSelector',
  props: {
    menu: {
      type: Object,
      default: () => ({}),
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    bottom: false,
    page: 1,
    stores: [],
    loading: false,
    selected: null,
  }),
  computed: {
    ...mapGetters('orders', ['draftMenuId', 'draftStoreId']),
    ...mapState('store', ['storesInfos']),
    allStores() {
      return this.storesInfos?.stores || [];
    },
    lastPage() {
      return this.storesInfos?.pagination?.total_page || 1;
    },
    menus() {
      return this.stores.reduce((acc, curr) => {
        const { menus, ...storeInfo } = curr;
        if (!menus) return acc;

        acc.push(...menus.reduce((itemAcc, menuItems) => {
          if (!menuItems.menu_contents.length) {
            return itemAcc;
          }
          const menuInfo = { ...menuItems };
          menuInfo.storeInfo = storeInfo;
          itemAcc.push(menuInfo);
          return itemAcc;
        }, []));
        return acc;
      }, []);
    },
    noMorePages() {
      return this.page >= this.lastPage;
    },
  },
  beforeDestroy() {
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  async created() {
    await this.initialize();
  },
  watch: {
    async bottom(isBottom) {
      if (isBottom && !this.noMorePages) {
        this.page += 1;
        await this.getStoreList({ page: this.page, withMenu: true });
      }
    },
    allStores: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal !== oldVal && newVal?.length) {
          this.stores = this.stores.concat(newVal);
        }
      },
    },
  },
  methods: {
    ...mapActions('store', {
      getStoreList: GET_STORE_LIST,
    }),
    ...mapMutations('orders', {
      updateDraftOrder: UPDATE_DRAFT_ORDER,
    }),
    bottomVisible(element) {
      const { scrollTop } = element;
      const visible = element.clientHeight;
      const pageHeight = element.scrollHeight;
      const BUFFER = 20;
      // NOTE: Add a buffer to trigger loading a bit earlier
      const bottomOfPage = visible + scrollTop + BUFFER >= pageHeight;
      return bottomOfPage || pageHeight < visible;
    },
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    close() {
      this.$emit('close');
    },
    onConfirm() {
      this.$emit('menu', this.selected);
      this.$emit('close');
    },
    onScroll(event) {
      const { target = {} } = event || {};
      this.bottom = this.bottomVisible(target);
    },
    async initialize() {
      try {
        this.loading = true;
        await this.getStoreList({ page: this.page, withMenu: true });
        const storeId = this.draftStoreId;
        const menuId = this.draftMenuId;
        if (storeId && menuId) {
          const { menus = [], ...infos } = this.stores?.find(({ id }) => id === storeId) || {};
          const menu = menus.find(({ id }) => id === menuId);
          if (menu) {
            this.onSelect({ ...menu, storeInfo: infos });
          }
        }
      } catch {
        Toast.open({
          message: '無法載入店家清單，請稍後再試',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    getMenuItems(menu, length = 3) {
      if (!menu.menu_contents) {
        return '';
      }
      return menu.menu_contents.slice(0, length).map(({ name }) => name).join('、');
    },
    transformClosedDays(days) {
      return weekdayTransformer(days.map(({ day }) => day));
    },
    humanizeDate(date) {
      if (!date) return '';
      return dayjs(date).format('YYYY/MM/DD');
    },
    onSelect(menu) {
      this.updateDraftOrder({
        menuId: menu?.id,
        storeId: menu?.storeInfo?.id,
      });
      this.$emit('input', menu);
    },
  },
};
